import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import PoweredByGoogle from 'assets/images/powered_by_google_on_white.png';
import clsx from 'clsx';
import { TextField } from 'components/TextField';
import { GOOGLE_API_KEY } from 'hooks/useGoogleMapsScript/useGoogleMapsScript';
import { Address } from 'olo-service/models';
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useTranslation } from 'react-i18next';
import { addressComponentsToAddress } from 'util/AddressUtil';
import { formatAddress } from 'util/Formatting';
import styles from './AddressField.module.scss';
import { DropdownItem } from './DropdownItem/DropdownItem';
import { FieldError } from './FieldError';
import { Icon } from './Icon';
import Typography from './Typography/Typography';
import { ADDRESS_FIELD_TEST_IDS } from './AddressField.const';

type AddressFieldProps = {
  addressValue: Address | undefined;
  onAddressChange: (address: Address | undefined) => void;
} & Omit<
  React.ComponentProps<typeof TextField>,
  'label' | 'inputRef' | 'errorMessage' | 'customError' | 'defaultValue' | 'onKeyPress' | 'value'
>;

/**
 * A text field that can be used to enter an address. Uses Google Places to autocomplete the address.
 * The address must be selected from the list of suggestions, any manual edits will set the address to undefined.
 * @param props.addressValue Address to be used for validation. Any non-null address will be considered valid.
 * @param props.onAddressChange A callback that is called when the address is changed. The callback will be sent undefined if the user manually edits the address.
 */
export const AddressField: FC<AddressFieldProps> = ({ addressValue, onAddressChange, className, ...props }) => {
  const { t } = useTranslation();

  const { placesService, placePredictions, getPlacePredictions } = usePlacesAutocompleteService({
    apiKey: GOOGLE_API_KEY,
    debounce: 100,
    sessionToken: true,
    options: {
      input: '',
      componentRestrictions: { country: ['us', 'ca'] },
      types: ['address'],
    },
  });

  const [isSearching, setIsSearching] = useState(false);
  const [addressText, setAddressText] = useState(formatAddress(addressValue));
  const [addressTextIsFromSelected, setAddressTextIsFromSelected] = useState(true);

  useEffect(() => {
    setAddressText(formatAddress(addressValue));
  }, [addressValue]);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    getPlacePredictions({
      input: newText,
    });
    setIsSearching(true);
    setAddressText(newText);
    setAddressTextIsFromSelected(false);
    onAddressChange(undefined);
  };

  const onPlaceDetails = (place: google.maps.places.PlaceResult) => {
    if (place?.address_components == null) {
      onAddressChange(undefined);

      return;
    }

    const address = addressComponentsToAddress(place.address_components);

    if (address == null) {
      onAddressChange(undefined);

      return;
    }

    onAddressChange(address);
  };

  const onPlaceSelected = (place: google.maps.places.AutocompletePrediction) => {
    setIsSearching(false);
    setAddressText(place.description);
    setAddressTextIsFromSelected(true);

    placesService?.getDetails(
      {
        placeId: place.place_id,
      },
      (placeDetails) => onPlaceDetails(placeDetails)
    );
  };

  const showError = useMemo(
    () => addressValue == null && !addressTextIsFromSelected && !isSearching,
    [addressTextIsFromSelected, addressValue, isSearching]
  );
  const showSuggestions = useMemo(
    () => isSearching && !!placePredictions.length,
    [isSearching, placePredictions.length]
  );

  // Logic for using arrow keys to select suggestions
  const suggestionsRef = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    if (showSuggestions) {
      suggestionsRef.current = suggestionsRef.current.slice(0, placePredictions.length);
    }
  }, [placePredictions.length, showSuggestions]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && showSuggestions) {
      const direction = e.key === 'ArrowDown' ? 1 : -1;

      const currentSuggestion = suggestionsRef.current.findIndex(
        (suggestion) => suggestion != null && document.activeElement === suggestion
      );

      const nextSuggestion = currentSuggestion + direction;
      if (currentSuggestion === -1 || nextSuggestion >= suggestionsRef.current.length) {
        suggestionsRef.current[0]?.focus();
      } else if (nextSuggestion < 0) {
        suggestionsRef.current[suggestionsRef.current.length - 1]?.focus();
      } else {
        suggestionsRef.current[nextSuggestion]?.focus();
      }
    }
  };

  return (
    <div
      className={styles.container}
      onKeyDown={onKeyDown}
      onBlur={() => setIsSearching(false)}
      onFocus={() => setIsSearching(true)}
      role="combobox"
      aria-expanded={showSuggestions}
      tabIndex={-1}
      aria-controls="addressField"
    >
      <TextField
        id="addressField"
        customError={showError}
        hideErrorText={true}
        required
        label={t('deliveryAddressModal.address')}
        onChange={handleTextFieldChange}
        onInvalid={() => setAddressTextIsFromSelected(false)}
        className={className}
        value={addressText}
        data-testid={ADDRESS_FIELD_TEST_IDS.textField}
        {...props}
      >
        {showSuggestions && (
          <div className={clsx(styles.placesContainer)}>
            {placePredictions.map((prediction, i) => (
              <DropdownItem
                className={styles.dropdownButton}
                ref={(el) => (suggestionsRef.current[i] = el)}
                onMouseDown={(e) => e.preventDefault()} // causes onClick to be called before onBlur
                onClick={() => onPlaceSelected(prediction)}
                key={prediction.place_id}
                data-testid={ADDRESS_FIELD_TEST_IDS.prediction(i)}
              >
                <div className={styles.item}>
                  <Icon className={styles.pinIcon} name="pin" size={24} />
                  <div className={styles.itemText}>
                    <Typography.Body>{prediction.structured_formatting.main_text}</Typography.Body>
                    <Typography.Body variant="tertiary">
                      {prediction.structured_formatting.secondary_text}
                    </Typography.Body>
                  </div>
                </div>
              </DropdownItem>
            ))}
            <div className={styles.googleContainer}>
              <img src={PoweredByGoogle} alt={t('poweredByGoogle')} />
            </div>
          </div>
        )}
        {showError && (
          <FieldError data-testid={ADDRESS_FIELD_TEST_IDS.error} className={styles.error} id={ADDRESS_FIELD_TEST_IDS.error}>
            {t('deliveryAddressModal.invalidAddress')}
          </FieldError>
        )}
      </TextField>
    </div>
  );
};
